import type { UiEnumOption } from "./uiPropertyEnums.ts";
import {
	FONT_OPTIONS,
	GRID_CORNER_OPTIONS,
	GRID_FILL_OPTIONS,
	LIST_ALIGNMENT_OPTIONS,
	LIST_DIRECTION_OPTIONS,
	LIST_SORT_OPTIONS,
	TEXT_TRUNCATE_OPTIONS,
	X_ALIGNMENT_OPTIONS,
	Y_ALIGNMENT_OPTIONS,
} from "./uiPropertyEnums.ts";

export type UiNodeTypeName =
	| "Frame"
	| "Text"
	| "Image"
	| "Viewport"
	| "Repeater"
	| "List"
	| "Grid"
	| "UICorner"
	| "UIStroke"
	| "UIPadding"
	| "UIListLayout"
	| "UIGradient";

export type UiPropertyValueKind =
	| "string"
	| "number"
	| "boolean"
	| "color"
	| "enum"
	| "edge"
	| "asset";

export type TokenUsage = "color" | "radius" | "Font" | "spacing" | "size" | "border" | "shadow" | "gradient";

export type UiPropertyGroup =
	| "Identity"
	| "Layout"
	| "Appearance"
	| "Border"
	| "Padding"
	| "Margin"
	| "Effects"
	| "Transform"
	| "Text"
	| "Image"
	| "Behavior"
	| "Advanced";

export type UiPropertySpec = {
	key: string;
	valueKind: UiPropertyValueKind;
	compatibleWith: readonly UiNodeTypeName[];
	label: { en: string; fr: string };
	helper?: { en: string; fr: string };
	group?: UiPropertyGroup;
	tokenUsage?: TokenUsage;
	enumValues?: readonly UiEnumOption[];
	min?: number;
	max?: number;
	step?: number;
	precision?: number;
	suffix?: string;
	edgeAxes?: readonly ("top" | "right" | "bottom" | "left")[];
	default?: unknown;
};

const ALL_NODES: readonly UiNodeTypeName[] = [
	"Frame",
	"Text",
	"Image",
	"Viewport",
	"Repeater",
	"List",
	"Grid",
];
const ALL_VISUAL_NODES: readonly UiNodeTypeName[] = ["Frame", "Text", "Image", "Viewport"];
const TEXT_NODES: readonly UiNodeTypeName[] = ["Text"];
const IMAGE_NODES: readonly UiNodeTypeName[] = ["Image"];
const VIEWPORT_NODES: readonly UiNodeTypeName[] = ["Viewport"];

export const UI_PROPERTY_REGISTRY: readonly UiPropertySpec[] = [
	{
		key: "Visible",
		valueKind: "boolean",
		compatibleWith: ALL_NODES,
		group: "Identity",
		default: true,
		label: { en: "Visible", fr: "Visible" },
	},

	{
		key: "ZIndex",
		valueKind: "number",
		compatibleWith: ALL_NODES,
		group: "Layout",
		min: 0,
		max: 100,
		step: 1,
		default: 0,
		label: { en: "Display order", fr: "Ordre d'affichage" },
		helper: {
			en: "Which element overlaps the others when they share space.",
			fr: "Quel élément passe devant les autres quand ils se chevauchent.",
		},
	},
	{
		key: "sizeOffsetX",
		valueKind: "number",
		compatibleWith: ALL_NODES,
		group: "Advanced",
		label: { en: "Width (px)", fr: "Largeur (px)" },
	},
	{
		key: "sizeOffsetY",
		valueKind: "number",
		compatibleWith: ALL_NODES,
		group: "Advanced",
		label: { en: "Height (px)", fr: "Hauteur (px)" },
	},
	{
		key: "width",
		valueKind: "number",
		compatibleWith: ALL_NODES,
		group: "Advanced",
		min: 0,
		max: 1,
		step: 0.01,
		precision: 2,
		label: { en: "Width in parent", fr: "Largeur dans le parent" },
		helper: {
			en: "Use 1 for full width, 0.5 for half.",
			fr: "Utilise 1 pour toute la largeur, 0,5 pour la moitié.",
		},
	},

	{
		key: "BackgroundColor3",
		valueKind: "color",
		compatibleWith: ALL_VISUAL_NODES,
		group: "Appearance",
		tokenUsage: "color",
		label: { en: "Background colour", fr: "Couleur de fond" },
	},
	{
		key: "BackgroundTransparency",
		valueKind: "number",
		compatibleWith: ALL_VISUAL_NODES,
		group: "Appearance",
		min: 0,
		max: 1,
		step: 0.05,
		precision: 2,
		default: 0,
		label: { en: "Background transparency", fr: "Transparence du fond" },
	},

	{
		key: "Text",
		valueKind: "string",
		compatibleWith: TEXT_NODES,
		group: "Text",
		default: "",
		label: { en: "Text content", fr: "Texte" },
	},
	{
		key: "TextColor3",
		valueKind: "color",
		compatibleWith: TEXT_NODES,
		group: "Text",
		tokenUsage: "color",
		default: { r: 255, g: 255, b: 255 },
		label: { en: "Text colour", fr: "Couleur du texte" },
	},
	{
		key: "Font",
		valueKind: "enum",
		compatibleWith: TEXT_NODES,
		group: "Text",
		enumValues: FONT_OPTIONS,
		default: "Gotham",
		label: { en: "Font", fr: "Police" },
	},
	{
		key: "TextXAlignment",
		valueKind: "enum",
		compatibleWith: TEXT_NODES,
		group: "Text",
		enumValues: X_ALIGNMENT_OPTIONS,
		default: "Left",
		label: { en: "Horizontal alignment", fr: "Alignement horizontal" },
	},
	{
		key: "TextYAlignment",
		valueKind: "enum",
		compatibleWith: TEXT_NODES,
		group: "Text",
		enumValues: Y_ALIGNMENT_OPTIONS,
		default: "Center",
		label: { en: "Vertical alignment", fr: "Alignement vertical" },
	},
	{
		key: "TextWrapped",
		valueKind: "boolean",
		compatibleWith: TEXT_NODES,
		group: "Text",
		default: false,
		label: { en: "Wrap on several lines", fr: "Texte sur plusieurs lignes" },
	},
	{
		key: "TextTruncate",
		valueKind: "enum",
		compatibleWith: TEXT_NODES,
		group: "Text",
		enumValues: TEXT_TRUNCATE_OPTIONS,
		default: "None",
		label: { en: "Truncate long text", fr: "Couper le texte trop long" },
	},
	{
		key: "TextSize",
		valueKind: "number",
		compatibleWith: TEXT_NODES,
		group: "Text",
		min: 1,
		max: 200,
		step: 1,
		suffix: "px",
		default: 14,
		label: { en: "Text size (px)", fr: "Taille du texte (px)" },
	},

	{
		key: "Image",
		valueKind: "asset",
		compatibleWith: IMAGE_NODES,
		group: "Image",
		label: { en: "Image asset", fr: "Image" },
	},
	{
		key: "assetId",
		valueKind: "asset",
		compatibleWith: IMAGE_NODES,
		group: "Image",
		label: { en: "Image asset (dynamic)", fr: "Image (dynamique)" },
	},
	{
		key: "ImageColor3",
		valueKind: "color",
		compatibleWith: IMAGE_NODES,
		group: "Image",
		tokenUsage: "color",
		default: { r: 255, g: 255, b: 255 },
		label: { en: "Image tint", fr: "Teinte" },
	},
	{
		key: "ImageTransparency",
		valueKind: "number",
		compatibleWith: IMAGE_NODES,
		group: "Image",
		min: 0,
		max: 1,
		step: 0.05,
		precision: 2,
		default: 0,
		label: { en: "Image transparency", fr: "Transparence image" },
	},

	{
		key: "itemId",
		valueKind: "string",
		compatibleWith: VIEWPORT_NODES,
		group: "Image",
		label: { en: "Item to display", fr: "Objet à afficher" },
	},
	{
		key: "modelPath",
		valueKind: "string",
		compatibleWith: VIEWPORT_NODES,
		group: "Image",
		label: { en: "3D model", fr: "Modèle 3D" },
		helper: {
			en: "Pick the 3D object that should appear here.",
			fr: "Choisis l'objet 3D qui doit apparaître ici.",
		},
	},

	{
		key: "direction",
		valueKind: "enum",
		compatibleWith: ["List"],
		group: "Layout",
		enumValues: LIST_DIRECTION_OPTIONS,
		default: "Vertical",
		label: { en: "Direction", fr: "Sens" },
		helper: {
			en: "How to stack children next to each other.",
			fr: "Comment empiler tes éléments les uns à côté des autres.",
		},
	},
	{
		key: "childPadding",
		valueKind: "number",
		compatibleWith: ["List"],
		group: "Layout",
		tokenUsage: "spacing",
		min: 0,
		max: 100,
		step: 1,
		suffix: "px",
		default: 0,
		label: { en: "Spacing between", fr: "Espace entre" },
	},
	{
		key: "alignment",
		valueKind: "enum",
		compatibleWith: ["List"],
		group: "Layout",
		enumValues: LIST_ALIGNMENT_OPTIONS,
		default: "Start",
		label: { en: "Alignment", fr: "Alignement" },
	},
	{
		key: "sortOrder",
		valueKind: "enum",
		compatibleWith: ["List"],
		group: "Layout",
		enumValues: LIST_SORT_OPTIONS,
		default: "LayoutOrder",
		label: { en: "Sort order", fr: "Tri" },
	},
	{
		key: "wraps",
		valueKind: "boolean",
		compatibleWith: ["List"],
		group: "Layout",
		default: false,
		label: { en: "Wrap when full", fr: "Passer à la ligne si plein" },
	},

	{
		key: "cellSizeX",
		valueKind: "number",
		compatibleWith: ["Grid"],
		group: "Layout",
		tokenUsage: "size",
		min: 0,
		max: 500,
		step: 1,
		suffix: "px",
		default: 64,
		label: { en: "Cell width", fr: "Largeur de case" },
		helper: {
			en: "All cells share the same size.",
			fr: "Tes éléments sont rangés dans des cases de même taille.",
		},
	},
	{
		key: "cellSizeY",
		valueKind: "number",
		compatibleWith: ["Grid"],
		group: "Layout",
		tokenUsage: "size",
		min: 0,
		max: 500,
		step: 1,
		suffix: "px",
		default: 64,
		label: { en: "Cell height", fr: "Hauteur de case" },
	},
	{
		key: "cellPaddingX",
		valueKind: "number",
		compatibleWith: ["Grid"],
		group: "Layout",
		tokenUsage: "spacing",
		min: 0,
		max: 100,
		step: 1,
		suffix: "px",
		default: 4,
		label: { en: "Horizontal spacing", fr: "Espace horizontal" },
	},
	{
		key: "cellPaddingY",
		valueKind: "number",
		compatibleWith: ["Grid"],
		group: "Layout",
		tokenUsage: "spacing",
		min: 0,
		max: 100,
		step: 1,
		suffix: "px",
		default: 4,
		label: { en: "Vertical spacing", fr: "Espace vertical" },
	},
	{
		key: "fillDirection",
		valueKind: "enum",
		compatibleWith: ["Grid"],
		group: "Layout",
		enumValues: GRID_FILL_OPTIONS,
		default: "Horizontal",
		label: { en: "Fill direction", fr: "Sens" },
	},
	{
		key: "startCorner",
		valueKind: "enum",
		compatibleWith: ["Grid"],
		group: "Layout",
		enumValues: GRID_CORNER_OPTIONS,
		default: "TopLeft",
		label: { en: "Start corner", fr: "Coin de départ" },
	},

{
		key: "CornerRadius",
		valueKind: "edge",
		compatibleWith: ["UICorner"],
		group: "Appearance",
		tokenUsage: "radius",
		label: { en: "Corner radius", fr: "Rayon des coins" },
	},

{
		key: "Color",
		valueKind: "color",
		compatibleWith: ["UIStroke"],
		group: "Appearance",
		tokenUsage: "color",
		label: { en: "Color", fr: "Couleur" },
	},
	{
		key: "Thickness",
		valueKind: "number",
		compatibleWith: ["UIStroke"],
		group: "Appearance",
		tokenUsage: "border",
		min: 0,
		max: 20,
		step: 1,
		suffix: "px",
		default: 1,
		label: { en: "Thickness", fr: "Épaisseur" },
	},
	{
		key: "Transparency",
		valueKind: "number",
		compatibleWith: ["UIStroke"],
		group: "Appearance",
		min: 0,
		max: 1,
		step: 0.01,
		precision: 2,
		default: 0,
		label: { en: "Transparency", fr: "Transparence" },
	},

{
		key: "PaddingTop",
		valueKind: "edge",
		compatibleWith: ["UIPadding"],
		group: "Padding",
		tokenUsage: "spacing",
		label: { en: "Top", fr: "Haut" },
	},
	{
		key: "PaddingRight",
		valueKind: "edge",
		compatibleWith: ["UIPadding"],
		group: "Padding",
		tokenUsage: "spacing",
		label: { en: "Right", fr: "Droite" },
	},
	{
		key: "PaddingBottom",
		valueKind: "edge",
		compatibleWith: ["UIPadding"],
		group: "Padding",
		tokenUsage: "spacing",
		label: { en: "Bottom", fr: "Bas" },
	},
	{
		key: "PaddingLeft",
		valueKind: "edge",
		compatibleWith: ["UIPadding"],
		group: "Padding",
		tokenUsage: "spacing",
		label: { en: "Left", fr: "Gauche" },
	},

{
		key: "FillDirection",
		valueKind: "enum",
		compatibleWith: ["UIListLayout"],
		group: "Layout",
		enumValues: LIST_DIRECTION_OPTIONS,
		default: "Vertical",
		label: { en: "Direction", fr: "Direction" },
	},
	{
		key: "Padding",
		valueKind: "edge",
		compatibleWith: ["UIListLayout"],
		group: "Layout",
		tokenUsage: "spacing",
		label: { en: "Gap", fr: "Espacement" },
	},
	{
		key: "HorizontalAlignment",
		valueKind: "enum",
		compatibleWith: ["UIListLayout"],
		group: "Layout",
		enumValues: X_ALIGNMENT_OPTIONS,
		default: "Left",
		label: { en: "Horizontal align", fr: "Alignement horizontal" },
	},
	{
		key: "VerticalAlignment",
		valueKind: "enum",
		compatibleWith: ["UIListLayout"],
		group: "Layout",
		enumValues: Y_ALIGNMENT_OPTIONS,
		default: "Top",
		label: { en: "Vertical align", fr: "Alignement vertical" },
	},
	{
		key: "SortOrder",
		valueKind: "enum",
		compatibleWith: ["UIListLayout"],
		group: "Layout",
		enumValues: LIST_SORT_OPTIONS,
		default: "LayoutOrder",
		label: { en: "Sort", fr: "Tri" },
	},
	{
		key: "Wraps",
		valueKind: "boolean",
		compatibleWith: ["UIListLayout"],
		group: "Layout",
		default: false,
		label: { en: "Wrap", fr: "Retour à la ligne" },
	},

{
		key: "Rotation",
		valueKind: "number",
		compatibleWith: ["UIGradient"],
		group: "Appearance",
		min: 0,
		max: 360,
		step: 1,
		suffix: "°",
		default: 0,
		label: { en: "Rotation", fr: "Rotation" },
	},
];

const UI_PROPERTY_BY_KEY: Record<string, UiPropertySpec> = Object.fromEntries(
	UI_PROPERTY_REGISTRY.map((p) => [p.key, p]),
);

export const UI_PROPERTY_KEY_ALIASES: Readonly<Record<string, string>> = {
	text: "Text",
	TextColor: "TextColor3",
	ImageColor: "ImageColor3",
	BackgroundColor: "BackgroundColor3",
	SizeX: "sizeOffsetX",
	SizeY: "sizeOffsetY",
};

export const UI_PROPERTY_KEYS: readonly string[] = UI_PROPERTY_REGISTRY.map(
	(p) => p.key,
);

export const normalizeUiPropertyKey = (key: string): string =>
	UI_PROPERTY_KEY_ALIASES[key] ?? key;

export const getUiPropertySpec = (
	key: string,
): UiPropertySpec | undefined => UI_PROPERTY_BY_KEY[normalizeUiPropertyKey(key)];

export const isValidUiPropertyKey = (key: string): boolean =>
	normalizeUiPropertyKey(key) in UI_PROPERTY_BY_KEY;

export const getUiPropertyValueKind = (
	key: string,
): UiPropertyValueKind | undefined => UI_PROPERTY_BY_KEY[normalizeUiPropertyKey(key)]?.valueKind;

export const getUiPropertiesForNodeType = (
	nodeType: UiNodeTypeName,
): readonly UiPropertySpec[] =>
	UI_PROPERTY_REGISTRY.filter((s) => s.compatibleWith.includes(nodeType));
